import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { formatEtDate } from '../services/timeService';

// Check if running in Electron
const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron;

// Create the context
const DataPersistenceContext = createContext(null);

// Custom hook to use the context
export const useDataPersistence = () => {
  const context = useContext(DataPersistenceContext);
  if (!context) {
    throw new Error('useDataPersistence must be used within a DataPersistenceProvider');
  }
  return context;
};

// Provider component
export const DataPersistenceProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const [dataPath, setDataPath] = useState(null);
  const [initialData, setInitialData] = useState({ trades: [], settings: null });
  
  const saveTimeoutRef = useRef(null);

  // Get the data storage path on mount
  useEffect(() => {
    const initialize = async () => {
      if (isElectron) {
        try {
          const path = await window.electronAPI.getDataPath();
          setDataPath(path);
          
          // Load trades
          const tradesResult = await window.electronAPI.loadTrades();
          const trades = tradesResult.success ? (tradesResult.trades || []) : [];
          
          // Load settings
          const settingsResult = await window.electronAPI.loadSettings();
          const settings = settingsResult.success ? settingsResult.settings : null;
          
          setInitialData({ trades, settings });
        } catch (err) {
          console.error('Error initializing data:', err);
          setError(err.message);
        }
      } else {
        setDataPath('localStorage');
        
        // Load from localStorage
        const storedTrades = localStorage.getItem('tradingJournal_trades');
        const storedSettings = localStorage.getItem('tradingJournal_settings');
        
        setInitialData({
          trades: storedTrades ? JSON.parse(storedTrades) : [],
          settings: storedSettings ? JSON.parse(storedSettings) : null
        });
      }
      
      setIsLoading(false);
    };

    initialize();
  }, []);

  // Save trades
  const saveTrades = useCallback(async (trades) => {
    setError(null);
    
    try {
      if (isElectron) {
        const result = await window.electronAPI.saveTrades(trades);
        if (result.success) {
          setLastSaved(new Date());
          return true;
        } else {
          throw new Error(result.error);
        }
      } else {
        localStorage.setItem('tradingJournal_trades', JSON.stringify(trades));
        setLastSaved(new Date());
        return true;
      }
    } catch (err) {
      console.error('Error saving trades:', err);
      setError(err.message);
      return false;
    }
  }, []);

  // Auto-save with debounce
  const autoSaveTrades = useCallback((trades) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveTrades(trades);
    }, 1000);
  }, [saveTrades]);

  // Save settings
  const saveSettings = useCallback(async (settings) => {
    try {
      if (isElectron) {
        const result = await window.electronAPI.saveSettings(settings);
        if (!result.success) {
          throw new Error(result.error);
        }
        return true;
      } else {
        localStorage.setItem('tradingJournal_settings', JSON.stringify(settings));
        return true;
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message);
      return false;
    }
  }, []);

  // Export trades
  const exportTrades = useCallback(async (trades) => {
    try {
      if (isElectron) {
        return await window.electronAPI.exportTrades(trades);
      } else {
        const blob = new Blob([JSON.stringify(trades, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trades_export_${formatEtDate(new Date())}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return { success: true };
      }
    } catch (err) {
      console.error('Error exporting trades:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    isLoading,
    lastSaved,
    error,
    dataPath,
    isElectron,
    initialData,
    saveTrades,
    autoSaveTrades,
    saveSettings,
    exportTrades
  };

  return (
    <DataPersistenceContext.Provider value={value}>
      {children}
    </DataPersistenceContext.Provider>
  );
};

export default DataPersistenceProvider;
